import DOMPurify from 'dompurify'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import Drawer from '../../../components/common/Drawer'
import Targets from '../../../components/common/Targets'
import { api } from '../../../configs/api'
import { useBusiness } from '../../../context/BusinessContext'
import BusinessForm from '../BusinessForm'
import BusinessDelete from './BusinessDelete'
import BusinessTimeLineSteps from './BusinessTimeLineSteps'
import ClientCompanyDetail from './ClientCompanyDetail'
import BusinessNotes from './Note/BusinessNotes'

interface BusinessDetailProps {
    open: boolean
    handleClose: () => void
}

export type BusinessType = {
    id: string
    title: string
    company?: any
    description?: string
    dateConclude?: string
    step: any
    contacts?: any
    products?: any
    targets?: any
    notes?: any
    dateNotify: string | null
    isExpired: boolean
}

const BusinessDetail = ({ open, handleClose }: BusinessDetailProps) => {
    const { openDetail, currentStep } = useBusiness()
    const [data, setData] = useState<BusinessType | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [openEdit, setOpenEdit] = useState(false)
    const [openDelete, setOpenDelete] = useState(false)
    const propsData = openDetail.data
    const bottomRef = useRef<HTMLDivElement | null>(null)

    const getData = async () => {
        if (!propsData?.id) return

        setLoading(true) // Inicia o carregamento

        try {
            const response = await fetch(`${api}business/getBusinessDetail`, {
                method: 'POST',
                body: JSON.stringify({ id: parseInt(propsData.id) })
            })
            const result = await response.json()
            if (!result.success) {
                return toast.error(result.message || 'Erro ao carregar negócio!')
            }

            setData(result.data)
        } catch (error) {
            console.log(error)
            toast.error('Erro ao buscar dados do negócio.')
        } finally {
            setLoading(false) // Finaliza o carregamento
        }
    }

    const handleConclude = (businessID: number, values: any) => {
        setOpenEdit(false)

        // Atualiza business no contexto
        openDetail.data = { ...openDetail.data, ...values }
        openDetail.data.id = businessID
    }

    useEffect(() => {
        if (open) getData()
    }, [propsData, open])

    if (!open) return null

    return (
        <>
            <Drawer
                open={open}
                handleClose={handleClose}
                title={
                    data ? (
                        <span>
                            {data.title}{' '}
                            <span className={`${data.isExpired ? 'text-red-500' : 'text-success-500'}`}>
                                #{data?.id}
                            </span>
                        </span>
                    ) : (
                        'Carregando...'
                    )
                }
                handleEdit={() => setOpenEdit(true)}
                handleDelete={() => setOpenDelete(true)}
                maxWidth='max-w-7xl'
            >
                <div className='drawer-scroll flex flex-col gap-3 text-gray-800 dark:text-gray-200 text-sm'>
                    {loading ? (
                        <p className='text-center text-gray-500'>Carregando detalhes...</p>
                    ) : (
                        data && (
                            <>
                                {/* Timeline fases */}
                                <BusinessTimeLineSteps data={data} />

                                {/* Produtos, Empresa e Contatos */}
                                <ClientCompanyDetail data={data} />

                                {/* Descrição */}
                                {data.description && (
                                    <p
                                        className='leading-relaxed'
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.description) }}
                                    />
                                )}

                                {/* Etiquetas */}
                                <div className='flex flex-col sm:flex-row items-center gap-4'>
                                    <Targets
                                        values={data.targets?.map((t: any) => ({ ...t, name: t.label })) || []}
                                        mode='view'
                                    />
                                </div>

                                {/* Notas */}
                                <BusinessNotes
                                    businessID={parseInt(data.id)}
                                    contacts={data?.contacts}
                                    companyID={data?.company?.id}
                                    data={data}
                                    setData={setData}
                                    scrollRef={bottomRef}
                                />

                                <div ref={bottomRef} />
                            </>
                        )
                    )}
                </div>
            </Drawer>

            <BusinessForm
                type='update'
                open={openEdit}
                handleClose={() => setOpenEdit(false)}
                handleConclude={handleConclude}
                pipeline={null}
                step={currentStep}
                data={data}
            />

            <BusinessDelete
                open={openDelete}
                handleClose={() => {
                    setOpenDelete(false)
                    handleClose()
                }}
                data={data}
            />
        </>
    )
}

export default BusinessDetail
